import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { inboxApi } from '@wa/shared';

/**
 * Available / Away.
 *
 * The agent's own switch, and the thing that decides whether automatic assignment
 * can hand them a conversation. It is in the header rather than buried in a settings
 * page because it is the one setting that changes several times a day, and because
 * an agent who cannot see their own state will not trust it.
 *
 * Deliberately shows the state as a word, not only as a colour: "the green one means
 * I get work" is a thing to remember, and roughly one man in twelve cannot read it
 * from the colour anyway.
 *
 * `compact` is the 56px rail, where there is no room for the word. The dot is then
 * carrying the state alone, which is the thing this component was written not to
 * do — so on that path the title attribute and the accessible name both say it in
 * words, and the switch keeps its `role` and `aria-checked`. A tooltip is a poorer
 * affordance than a label and it is not nothing.
 */
export function AvailabilityToggle({ compact = false }: { compact?: boolean } = {}) {
  const queryClient = useQueryClient();

  const state = useQuery({
    queryKey: ['availability'],
    queryFn: inboxApi.availability,
    staleTime: 60_000,
  });

  const available = state.data?.availability === 1;

  const set = useMutation({
    mutationFn: inboxApi.setAvailability,
    onMutate: async (next: boolean) => {
      // Optimistic: the switch must move under the finger. A toggle that waits for
      // a round trip gets pressed twice.
      await queryClient.cancelQueries({ queryKey: ['availability'] });
      const previous = queryClient.getQueryData(['availability']);
      queryClient.setQueryData(['availability'], { availability: next ? 1 : 0, changedAt: null });
      return { previous };
    },
    onError: (_error, _next, context) => {
      if (context) queryClient.setQueryData(['availability'], context.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['availability'] }),
  });

  // Nothing until the state is known — a switch that renders Away and then jumps to
  // Available reads as though something changed it.
  if (state.isPending || state.isError) return null;

  return (
    <button
      type="button"
      role="switch"
      aria-checked={available}
      disabled={set.isPending}
      onClick={() => set.mutate(!available)}
      title={available
        ? 'You can be given new conversations. Tap to stop.'
        : 'New conversations will not be assigned to you. Tap to start.'}
      aria-label={available ? 'Available for new conversations' : 'Away'}
      className={[
        'tap flex shrink-0 items-center rounded-full border font-medium transition',
        compact ? 'size-7 justify-center p-0' : 'gap-1.5 px-2.5 py-1 text-small',
        available
          ? 'border-(--color-good) bg-(--color-brand-wash) text-(--color-good)'
          : 'border-(--color-line) bg-(--color-surface) text-(--color-muted) hover:bg-(--color-sunken)',
      ].join(' ')}
    >
      <span
        aria-hidden="true"
        className={[
          'shrink-0 rounded-full',
          compact ? 'size-2.5' : 'size-2',
          available ? 'bg-(--color-good)' : 'bg-(--color-faint)',
        ].join(' ')}
      />
      {!compact && (
        <span className="whitespace-nowrap">{available ? 'Available' : 'Away'}</span>
      )}
    </button>
  );
}
